import * as Sentry from '@sentry/react';
import { Component } from 'react';
import { withRouter } from 'react-router';

import PropTypes from 'prop-types';

import { Box, Button, Typography } from '@mui/material';

import { loadUser } from '../../utils';
import { findPersonEmail } from '../Person/utils';

import { ActionButton } from '../Button';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { eventId: null };
    const { history } = this.props;

    history.listen(() => {
      const { hasError } = this.state;
      if (hasError) {
        this.setState({
          hasError: false,
        });
      }
    });

    this.showReportDialog = this.showReportDialog.bind(this);
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    Sentry.withScope((scope) => {
      scope && scope.setExtras(errorInfo);
      const eventId = Sentry.captureException(error);
      this.setState({ eventId });
    });
    // handle 'Loading chunk' error by refreshing the page
    // occurs for some users when trying to load js chunk of the prev version
    if (error?.message?.includes('Loading chunk')) {
      window.location.reload();
    }
  }

  showReportDialog() {
    const user = loadUser();
    const name = user ? `${user.firstName} ${user.lastName}` : null;
    const email = findPersonEmail(user);
    const { eventId } = this.state;
    Sentry.showReportDialog({
      onLoad: () => {
        const email = document.getElementById('id_email');
        const name = document.getElementById('id_name');

        const parentEmail = email.parentNode;
        const parentName = name.parentNode;
        if (parentEmail) parentEmail.style.display = 'none';
        if (parentName) parentName.style.display = 'none';

        const poweredBy = document.getElementsByClassName('powered-by');
        const submitContainer = document.getElementsByClassName('form-submit');
        if (poweredBy) poweredBy[0].style.display = 'none';
        if (submitContainer)
          submitContainer[0].style =
            'display: flex; justify-content: start; align-items: center;';

        const sentryWrapper = document.getElementsByClassName(
          'sentry-error-embed-wrapper',
        );
        if (sentryWrapper) sentryWrapper[0].style = `z-index: 1200`;
      },
      lang: 'en',
      eventId,
      title: 'We are working to resolve this issue',
      subtitle: 'Any notes on what happened will help us resolve it quickly',
      subtitle2: '',
      labelSubmit: 'Submit Notes',
      labelComments: 'Add your notes below',
      user: { email, name },
    });
  }

  render() {
    const { history, children, isPageReloadAllowed = false } = this.props;
    const { hasError } = this.state;
    if (hasError) {
      return (
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          width="100%"
          height="100%"
          gap="1rem"
          px="3rem"
        >
          <Typography variant="h5" align="center">
            Oops… There was an error processing your request. Our team has been
            notified.
          </Typography>
          <Typography variant="h6" align="center">
            Please help us by providing additional information
          </Typography>
          <Box
            display="flex"
            flexWrap="wrap"
            justifyContent="center"
            gap="1rem"
            my="2rem"
          >
            <ActionButton
              variant="contained"
              disableElevation
              onClick={() => this.showReportDialog()}
            >
              Send Additional Information
            </ActionButton>
            <Button
              color="primary"
              variant="contained"
              disableElevation
              onClick={() => history.goBack()}
            >
              Go Back
            </Button>
            {isPageReloadAllowed && (
              <Button
                color="primary"
                variant="contained"
                disableElevation
                onClick={() => window.location.reload()}
              >
                Reload this page
              </Button>
            )}
          </Box>
        </Box>
      );
    }
    return children;
  }
}

ErrorBoundary.propTypes = {
  history: PropTypes.instanceOf(Object),
  children: PropTypes.instanceOf(Object),
  isPageReloadAllowed: PropTypes.bool,
};

export default withRouter(ErrorBoundary);
